import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, {apiCallWithToast} from '../../services/api';

// Async Thunks for CRUD operations

// Fetch all academic years
export const fetchAcademicYears = createAsyncThunk(
  'academicYears/fetchAcademicYears',
  async () => {
    const response = await api.get(`academic-years`);
    return response.data;
  }
);

// Add a new academic year
export const addAcademicYear = createAsyncThunk(
  'academicYears/addAcademicYear',
  async (newYear) => {
    const response = await apiCallWithToast(api.post(`academic-years`, newYear));
    return response.data;
  }
);

// Update an existing academic year
export const updateAcademicYear = createAsyncThunk(
  'academicYears/updateAcademicYear',
  async (updatedYear) => {
    const response = await apiCallWithToast(api.put(`academic-years/${updatedYear.academic_id}`, updatedYear));
    return response.data;
  }
);

// Delete an academic year
export const deleteAcademicYear = createAsyncThunk(
  'academicYears/deleteAcademicYear',
  async (id) => {
    await apiCallWithToast(api.delete(`academic-years/${id}`));
    return id;
  }
);

// Fetch academic year statistics
export const fetchAcademicYearStatistics = createAsyncThunk(
  'academicYears/fetchStatistics',
  async () => {
    const response = await api.get('academic-years/statistics');
    return response.data;
  }
);

// Set current academic year
export const setCurrentAcademicYear = createAsyncThunk(
  'academicYears/setCurrent',
  async (id) => {
    const response = await apiCallWithToast(api.post(`academic-years/${id}/set-current`));
    return { id, data: response.data };
  }
);

// Duplicate academic year
export const duplicateAcademicYear = createAsyncThunk(
  'academicYears/duplicate',
  async ({ id, data }) => {
    const response = await apiCallWithToast(api.post(`academic-years/${id}/duplicate`, data));
    return response.data;
  }
);

// Get current academic year
export const getCurrentAcademicYear = createAsyncThunk(
  'academicYears/getCurrent',
  async () => {
    const response = await api.get('academic-years/current');
    return response.data;
  }
);

export const academic = createSlice({
  name: 'academic',
  initialState: {
    items: {},
    statistics: null,
    currentYear: null,
    ui: {
      fetchAcademicYears: { loading: true, error: false },
      addAcademicYear: { loading: false, error: false },
      updateAcademicYear: { loading: false, error: false },
      deleteAcademicYear: { loading: false, error: false },
      fetchStatistics: { loading: false, error: false },
      setCurrent: { loading: false, error: false },
      duplicate: { loading: false, error: false },
      getCurrent: { loading: false, error: false },
    },
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetUI: (state) => {
      Object.keys(state.ui).forEach(key => {
        state.ui[key] = { loading: false, error: false };
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Academic Years
      .addCase(fetchAcademicYears.pending, (state) => {
        state.ui.fetchAcademicYears.loading = true;
        state.ui.fetchAcademicYears.error = false;
      })
      .addCase(fetchAcademicYears.fulfilled, (state, action) => {
        state.ui.fetchAcademicYears.loading = false;
        state.ui.fetchAcademicYears.error = false;
        state.items = action.payload;
      })
      .addCase(fetchAcademicYears.rejected, (state, action) => {
        state.ui.fetchAcademicYears.loading = false;
        state.ui.fetchAcademicYears.error = true;
        state.error = action.error.message;
      })
      
      // Add Academic Year
      .addCase(addAcademicYear.pending, (state) => {
        state.ui.addAcademicYear.loading = true;
        state.ui.addAcademicYear.error = false;
      })
      .addCase(addAcademicYear.fulfilled, (state, action) => {
        state.ui.addAcademicYear.loading = false;
        state.ui.addAcademicYear.error = false;
        // Update statistics if available
        if (state.statistics) {
          state.statistics.total = (state.statistics.total || 0) + 1;
          if (action.payload.status === 'active') {
            state.statistics.active = (state.statistics.active || 0) + 1;
          }
        }
      })
      .addCase(addAcademicYear.rejected, (state, action) => {
        state.ui.addAcademicYear.loading = false;
        state.ui.addAcademicYear.error = true;
        state.error = action.error.message;
      })
      
      // Update Academic Year
      .addCase(updateAcademicYear.pending, (state) => {
        state.ui.updateAcademicYear.loading = true;
        state.ui.updateAcademicYear.error = false;
      })
      .addCase(updateAcademicYear.fulfilled, (state, action) => {
        state.ui.updateAcademicYear.loading = false;
        state.ui.updateAcademicYear.error = false;
        // Update current year if it was updated
        if (action.payload.is_current) {
          state.currentYear = action.payload;
        }
      })
      .addCase(updateAcademicYear.rejected, (state, action) => {
        state.ui.updateAcademicYear.loading = false;
        state.ui.updateAcademicYear.error = true;
        state.error = action.error.message;
      })
      
      // Delete Academic Year
      .addCase(deleteAcademicYear.pending, (state) => {
        state.ui.deleteAcademicYear.loading = true;
        state.ui.deleteAcademicYear.error = false;
      })
      .addCase(deleteAcademicYear.fulfilled, (state, action) => {
        state.ui.deleteAcademicYear.loading = false;
        state.ui.deleteAcademicYear.error = false;
        // Update statistics if available
        if (state.statistics) {
          state.statistics.total = Math.max(0, (state.statistics.total || 0) - 1);
        }
      })
      .addCase(deleteAcademicYear.rejected, (state, action) => {
        state.ui.deleteAcademicYear.loading = false;
        state.ui.deleteAcademicYear.error = true;
        state.error = action.error.message;
      })
      
      // Fetch Statistics
      .addCase(fetchAcademicYearStatistics.pending, (state) => {
        state.ui.fetchStatistics.loading = true;
        state.ui.fetchStatistics.error = false;
      })
      .addCase(fetchAcademicYearStatistics.fulfilled, (state, action) => {
        state.ui.fetchStatistics.loading = false;
        state.ui.fetchStatistics.error = false;
        state.statistics = action.payload;
        if (action.payload.current) {
          state.currentYear = action.payload.current;
        }
      })
      .addCase(fetchAcademicYearStatistics.rejected, (state, action) => {
        state.ui.fetchStatistics.loading = false;
        state.ui.fetchStatistics.error = true;
        state.error = action.error.message;
      })
      
      // Set Current
      .addCase(setCurrentAcademicYear.pending, (state) => {
        state.ui.setCurrent.loading = true;
        state.ui.setCurrent.error = false;
      })
      .addCase(setCurrentAcademicYear.fulfilled, (state, action) => {
        state.ui.setCurrent.loading = false;
        state.ui.setCurrent.error = false;
        state.currentYear = action.payload.data;
      })
      .addCase(setCurrentAcademicYear.rejected, (state, action) => {
        state.ui.setCurrent.loading = false;
        state.ui.setCurrent.error = true;
        state.error = action.error.message;
      })
      
      // Duplicate
      .addCase(duplicateAcademicYear.pending, (state) => {
        state.ui.duplicate.loading = true;
        state.ui.duplicate.error = false;
      })
      .addCase(duplicateAcademicYear.fulfilled, (state, action) => {
        state.ui.duplicate.loading = false;
        state.ui.duplicate.error = false;
        // Update statistics if available
        if (state.statistics) {
          state.statistics.total = (state.statistics.total || 0) + 1;
          state.statistics.draft = (state.statistics.draft || 0) + 1;
        }
      })
      .addCase(duplicateAcademicYear.rejected, (state, action) => {
        state.ui.duplicate.loading = false;
        state.ui.duplicate.error = true;
        state.error = action.error.message;
      })
      
      // Get Current
      .addCase(getCurrentAcademicYear.pending, (state) => {
        state.ui.getCurrent.loading = true;
        state.ui.getCurrent.error = false;
      })
      .addCase(getCurrentAcademicYear.fulfilled, (state, action) => {
        state.ui.getCurrent.loading = false;
        state.ui.getCurrent.error = false;
        state.currentYear = action.payload;
      })
      .addCase(getCurrentAcademicYear.rejected, (state, action) => {
        state.ui.getCurrent.loading = false;
        state.ui.getCurrent.error = true;
        state.error = action.error.message;
      });
  },
});

export const { clearError, resetUI } = academic.actions;

export default academic.reducer;
